import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Form,
  FormLayoutGroup,
  Heading,
  Link,
  List,
  ListItem,
  Paragraph,
  Radio,
  RadioGroup,
  Stack,
  Text,
  TextField,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import plur from 'plur';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import { createJoinRequest, signupResumeHref, type JoinRequestAccounts } from '@/services/join-request/api';
import * as styles from '../index.css';

const schema = z.object({
  firstName: z.string().trim().min(1, 'Enter your first name.'),
  lastName: z.string().trim(),
});

type OrgPickerStepProps = {
  token: string;
  data: JoinRequestAccounts;
  /** The org the request was sent to (by name), so the confirmation can say who was notified. */
  onSent: (accountName: string) => void;
  /** The token expired or was consumed elsewhere — show the link-expired state. */
  onTokenExpired: () => void;
};

/**
 * The org picker: the accounts on the requester's verified email domain, each
 * with its member count, plus the requester's name so an admin can recognize
 * who's asking.
 */
export function OrgPickerStep({ token, data, onSent, onTokenExpired }: OrgPickerStepProps) {
  const snackbars = useSnackbars();
  const [selectedAccountId, setSelectedAccountId] = useState(data.accounts[0]?.account_id ?? '');

  const form = useZodForm({
    schema,
    defaultValues: { firstName: '', lastName: '' },
    mode: 'onSubmit',
  });

  const sendRequest = useMutation({
    mutationFn: (values: z.infer<typeof schema>) =>
      createJoinRequest(token, {
        accountId: selectedAccountId,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      }),
  });

  const selectedAccount = data.accounts.find((account) => account.account_id === selectedAccountId);

  const onSubmit = form.handleSubmit(async (values) => {
    if (selectedAccount === undefined) return;
    try {
      await sendRequest.mutateAsync(values);
      onSent(selectedAccount.name);
    } catch (error) {
      // A 404 means the token expired or was consumed elsewhere → link-expired.
      if (error instanceof HydraUIAPIError && error.status === 404) {
        onTokenExpired();
        return;
      }
      // Any other failure surfaces as a transient error snackbar (Pluma forms
      // shouldn't show a standalone error banner). Swallowed here so the form's
      // submit promise doesn't reject into an unhandled rejection.
      snackbars.create({
        variant: 'error',
        id: 'joinRequest.create.error',
        message: "Your request couldn't be sent. Try again.",
      });
    }
  });

  // Return the pending promise so Pluma <Form>'s autoLoading locks the fields
  // and guards Enter-key double submits while the request is in flight.
  return (
    <Form onSubmit={onSubmit}>
      <Stack gap="300">
        <Stack gap="100">
          <Heading level="1" size="m" data-test-join-request-heading>
            Join your team
          </Heading>
          <Paragraph color="subtle">
            Your organization is already on Customer.io. Pick your team and we'll notify an admin that{' '}
            <Text weight="semibold">{data.email}</Text> wants to join. An admin will then set up your access.
          </Paragraph>
        </Stack>

        <RadioGroup
          ariaLabel="Organization to join"
          value={selectedAccountId}
          onChange={(value) => setSelectedAccountId(value)}
        >
          <List styleType="none" m="0" p="0">
            {data.accounts.map((account) => (
              <ListItem key={account.account_id} className={styles.orgListItem} data-test-join-request-org>
                <Radio label={account.name} value={account.account_id} />
                <Box pl="250">
                  <Text size="s" color="subtle" data-test-join-request-org-members>
                    {account.member_count} team {plur('member', account.member_count)}
                  </Text>
                </Box>
              </ListItem>
            ))}
          </List>
        </RadioGroup>

        <FormLayoutGroup>
          <TextField
            {...form.registerPluma('firstName')}
            label="First name"
            autoComplete="given-name"
            data-test-join-request-first-name
          />
          <TextField
            {...form.registerPluma('lastName')}
            label="Last name"
            autoComplete="family-name"
            data-test-join-request-last-name
          />
        </FormLayoutGroup>

        <Stack gap="150">
          <Button type="submit" variant="primary" isLoading={sendRequest.isPending} data-test-join-request-submit>
            Request access
          </Button>
          <Box textAlign="center">
            <Text size="s">
              <Link href={signupResumeHref(data.email)} data-test-join-request-continue-signup>
                Continue creating a new account instead
              </Link>
            </Text>
          </Box>
        </Stack>
      </Stack>
    </Form>
  );
}
